/**
 * DRPX Courier — Settings-Hook
 *
 * Dark Mode, Wartungsmodus, Sprache, Cookie-Consent, E-Mail-Notifications,
 * Onboarding. Persistiert Einstellungen in localStorage.
 *
 * @module hooks/useSettings
 */

import { useState, useCallback, useEffect } from 'react';
import { safeGetLS, safeSetLS, safeParseJSON } from '../lib/storage.js';
import {
  readThemeMode,
  saveThemeMode,
  removeLegacyThemeKey,
  nextThemeMode,
  systemPrefersDark,
  watchSystemDark,
  resolveDark,
  type ThemeMode,
} from '../lib/theme';

// ════════════════════════════════════════════════════════════════════
// Konstanten
// ════════════════════════════════════════════════════════════════════

const LANGS = ['de', 'en', 'pl', 'bg', 'ro', 'tr', 'ru'] as const;

const LANG_LABELS: Record<string, string> = {
  de: 'DE', en: 'EN', pl: 'PL', bg: 'BG', ro: 'RO', tr: 'TR', ru: 'RU',
};

const LANG_FLAGS: Record<string, string> = {
  de: '🇩🇪', en: '🇬🇧', pl: '🇵🇱', bg: '🇧🇬', ro: '🇷🇴', tr: '🇹🇷', ru: '🇷🇺',
};

const LANG_NAMES: Record<string, string> = {
  de: 'Deutsch', en: 'English', pl: 'Polski',
  bg: 'Български', ro: 'Română', tr: 'Türkçe', ru: 'Русский',
};

// ════════════════════════════════════════════════════════════════════
// Lokale Interfaces
// ════════════════════════════════════════════════════════════════════

/**
 * i18n-Kontext von `useT()` — eine Funktion mit Zusatz-Properties.
 * Die echte Typisierung kommt, wenn `i18n/index.jsx` migriert wird.
 */
interface I18nContext {
  (key: string): string;
  lang?: string;
  setLang?: (lang: string) => void;
}

interface CookieConsent {
  essential: boolean;
  functional?: boolean;
  analytics?: boolean;
}

interface UseSettingsReturn {
  WARTUNG_PASSWORT: string;
  wartungFreigeschaltet: boolean;
  wartungInput: string;
  setWartungInput: (v: string) => void;
  wartungPruefen: () => void;
  /** Ergebnis aus Stufe + Geräte-Einstellung: `true` = dunkel. */
  darkMode: boolean;
  /** Gewählte Stufe (Auto / Hell / Dunkel). */
  themeMode: ThemeMode;
  /** Knopf: Auto → Hell → Dunkel → Auto. */
  cycleThemeMode: () => void;
  neutralBg: boolean;
  toggleNeutralBg: () => void;
  lang: string;
  setLang: (l: string) => void;
  langOpen: boolean;
  setLangOpen: (v: boolean) => void;
  LANGS: readonly string[];
  LANG_LABELS: Record<string, string>;
  LANG_FLAGS: Record<string, string>;
  LANG_NAMES: Record<string, string>;
  cookieConsent: CookieConsent | null;
  handleCookieConsent: (consent: CookieConsent) => void;
  emailNotifs: boolean;
  toggleEmailNotifs: () => void;
  onboardingStep: number;
  setOnboardingStep: (v: number) => void;
  finishOnboarding: () => void;
}

// ════════════════════════════════════════════════════════════════════
// HOOK
// ════════════════════════════════════════════════════════════════════

export function useSettings(t: I18nContext): UseSettingsReturn {
  // ═══ WARTUNGSMODUS ═══
  const WARTUNG_PASSWORT = ''; // Leer = deaktiviert
  const [wartungFreigeschaltet, setWartungFreigeschaltet] = useState(function () {
    try {
      return safeGetLS('drpx_wartung') === WARTUNG_PASSWORT;
    } catch {
      return false;
    }
  });
  const [wartungInput, setWartungInput] = useState('');
  const wartungPruefen = function () {
    if (wartungInput === WARTUNG_PASSWORT) {
      setWartungFreigeschaltet(true);
      try { safeSetLS('drpx_wartung', WARTUNG_PASSWORT); } catch { /* ignored */ }
    }
  };

  // ═══ DARK MODE (10.10.2026: Auto / Hell / Dunkel, siehe lib/theme.ts) ═══
  const [themeMode, setThemeMode] = useState<ThemeMode>(function () {
    removeLegacyThemeKey();
    return readThemeMode();
  });
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark);
  // Geräte-Einstellung live verfolgen — „Auto" wechselt auch bei offener App.
  useEffect(function () {
    return watchSystemDark(setSystemDark);
  }, []);
  const darkMode = resolveDark(themeMode, systemDark);
  const cycleThemeMode = useCallback(function () {
    setThemeMode(function (m) {
      const n = nextThemeMode(m);
      saveThemeMode(n);
      return n;
    });
  }, []);

  // ═══ NEUTRAL BACKGROUND ═══
  const [neutralBg, setNeutralBg] = useState(function () {
    try { return safeGetLS('drpx_neutralbg') === '1'; } catch { return false; }
  });
  const toggleNeutralBg = useCallback(function () {
    setNeutralBg(function (v) {
      const n = !v;
      try { safeSetLS('drpx_neutralbg', n ? '1' : '0'); } catch { /* ignored */ }
      return n;
    });
  }, []);

  // ═══ LANGUAGE ═══
  const lang = t.lang || 'de';
  const setLang = useCallback(
    function (l: string) {
      if (t.setLang) t.setLang(l);
    },
    [t],
  );
  const [langOpen, setLangOpen] = useState(false);

  // ═══ COOKIE CONSENT ═══
  const [cookieConsent, setCookieConsent] = useState<CookieConsent | null>(function () {
    try {
      const s = safeGetLS('drpx_cookie_consent');
      return safeParseJSON<CookieConsent>(s) ?? null;
    } catch {
      return null;
    }
  });
  const handleCookieConsent = useCallback(function (consent: CookieConsent) {
    setCookieConsent(consent);
    try { safeSetLS('drpx_cookie_consent', JSON.stringify(consent)); } catch { /* ignored */ }
  }, []);

  // ═══ EMAIL NOTIFICATIONS TOGGLE ═══
  const [emailNotifs, setEmailNotifs] = useState(function () {
    try { return safeGetLS('drpx_email_notifs') !== 'off'; } catch { return true; }
  });
  const toggleEmailNotifs = useCallback(function () {
    setEmailNotifs(function (prev) {
      const next = !prev;
      try { safeSetLS('drpx_email_notifs', next ? 'on' : 'off'); } catch { /* ignored */ }
      return next;
    });
  }, []);

  // ═══ ONBOARDING ═══
  const [onboardingStep, setOnboardingStep] = useState(function () {
    try { return safeGetLS('drpx_onboarded') ? -1 : 0; } catch { return -1; }
  });
  const finishOnboarding = useCallback(function () {
    setOnboardingStep(-1);
    try { safeSetLS('drpx_onboarded', '1'); } catch { /* ignored */ }
  }, []);

  return {
    WARTUNG_PASSWORT,
    wartungFreigeschaltet, wartungInput, setWartungInput, wartungPruefen,
    darkMode, themeMode, cycleThemeMode,
    neutralBg, toggleNeutralBg,
    lang, setLang, langOpen, setLangOpen,
    LANGS, LANG_LABELS, LANG_FLAGS, LANG_NAMES,
    cookieConsent, handleCookieConsent,
    emailNotifs, toggleEmailNotifs,
    onboardingStep, setOnboardingStep, finishOnboarding,
  };
}
